import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import _ from 'lodash';

import {
    StyledCard,
    StyledCardContent,
    Text,
    OutlinedButton,
    StyledFormHelperText,
    ProviderButton,
} from '../../index';

import SelectStaffDialog from '../Dialog/SelectStaffDialog';

import { GET_PROVIDERS } from '../../../services/provider';

import { alertError } from '../../../redux/stateActions';

const ProviderCard = ({ errors, loading, selectedStaff, handleSelectStaff, disabled }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [open, setOpen] = useState(false);

    /*--------------- Staff List ---------------*/
    const [providerListData, setProviderListData] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const {
        error: getStaffError,
        data: getStaffData,
        loading: getStaffDataLoading,
    } = useQuery(GET_PROVIDERS, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user) || user.staff.staff_role.name === 'Provider',
    });
    useEffect(() => {
        if (getStaffError) {
            dispatch(alertError(`Cannot Get Staff Data: ${getStaffError.message}`));
            return;
        }

        if (getStaffData) {
            const newStaffOptions = getStaffData.staffs
                .filter((staff) => staff.deactivated === false && staff.user !== null)
                .map((staff) => ({
                    value: staff?.id,
                    text: staff?.user?.fullname,
                    avatar: staff?.user?.avatar?.url,
                }));

            if (searchValue === '') {
                setProviderListData(newStaffOptions);
            } else {
                const newPatientList = [...newStaffOptions].filter((staff) =>
                    staff.text.toLowerCase().includes(searchValue.toLowerCase())
                );
                setProviderListData(newPatientList);
            }
        }
    }, [getStaffError, getStaffData, searchValue]);

    return (
        <StyledCard style={{ position: 'relative' }}>
            <SelectStaffDialog
                open={open}
                selectedStaff={selectedStaff}
                onSelect={handleSelectStaff}
                onClose={() => setOpen(false)}
                user={user}
                providerListData={providerListData}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                loading={getStaffDataLoading}
            />
            <StyledCardContent style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Text $fontSize="20px" $bold>
                        Provider
                    </Text>
                    <OutlinedButton
                        $width="max-content"
                        $height="32px"
                        $noShadow
                        onClick={() => setOpen(true)}
                        disabled={disabled || loading}
                    >
                        {selectedStaff ? 'Edit' : '+ Add Provider'}
                    </OutlinedButton>
                </div>
                {selectedStaff && (
                    <ProviderButton
                        data={providerListData.find((item) => item.value === selectedStaff)}
                        user={user}
                        $avatarSize="46px"
                        buttonStyle={{
                            height: '74px',
                            border: '1px solid #DDE1F0',
                            borderRadius: '16px',
                            pointerEvents: 'none',
                        }}
                        containerStyle={{ padding: '15px 20px' }}
                    />
                )}
            </StyledCardContent>
            {errors.find((value) => value.property === `selectedProvider`) && (
                <StyledFormHelperText error>
                    {errors.find((value) => value.property === `selectedProvider`)?.message}
                </StyledFormHelperText>
            )}
        </StyledCard>
    );
};

ProviderCard.propTypes = {
    errors: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    selectedStaff: PropTypes.string,
    handleSelectStaff: PropTypes.func,
};

export default ProviderCard;
